import { useState, useCallback } from "react";
import { CustomerData, useCustomerSearch } from "./useCustomerSearch";

export type View = "selection" | "form" | "ledger" | "bom";

interface UsePopupScreenProps {
  initialSearchTerm?: string;
  initialPage?: number;
}

export const usePopupScreen = ({ initialSearchTerm = "", initialPage = 1 }: UsePopupScreenProps = {}) => {
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(null);
  const [view, setView] = useState<View>("selection");
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  const { customers, total, loading, addCustomerLocally } = useCustomerSearch(searchTerm, pageNumber);

  const selectCustomer = useCallback((customer: CustomerData) => {
    setSelectedCustomer(customer);
    setView("ledger");
  }, []);

  const addCustomer = useCallback(
    (customer: CustomerData) => {
      addCustomerLocally(customer);
      setSelectedCustomer(customer);
      setPageNumber(1); // 👈 Reset to first page so new customer is visible
      setView("selection");
    },
    [addCustomerLocally]
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPageNumber(1);
  }, []);

  const goBack = useCallback(() => setView("selection"), []);
  const goToForm = useCallback(() => setView("form"), []);
  const goToLedger = useCallback(() => setView("ledger"), []);
  const goToBom = useCallback(() => setView("bom"), []);

  return {
    selectedCustomer,
    view,
    pageNumber,
    searchTerm,
    customers,
    total,
    loading,
    selectCustomer,
    addCustomer,
    handleSearchChange,
    setPageNumber,
    goBack,
    goToForm,
    goToLedger,
    goToBom,
  };
};
